/**
 * Convert hex color to RGB format.
 * @param {string} hex - The hex color string.
 * @returns {object} - The RGB representation of the color.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

/**
 * Convert RGB color to hex format.
 * @param {object} rgb - The RGB representation of the color.
 * @returns {string} - The hex color string.
 */
function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Generate gradient colors between two hex colors.
 * @param {string} startHex - The starting hex color.
 * @param {string} endHex - The ending hex color.
 * @param {number} steps - The number of gradient steps.
 * @returns {string[]} - An array of hex color strings representing the gradient.
 */
export function generateGradientColors(startHex: string, endHex: string, steps: number): string[] {
  const startRgb = hexToRgb(startHex);
  const endRgb = hexToRgb(endHex);
  const gradient: string[] = [];

  for (let i = 0; i <= steps; i++) {
    const r = Math.round(startRgb.r + ((endRgb.r - startRgb.r) * i) / steps);
    const g = Math.round(startRgb.g + ((endRgb.g - startRgb.g) * i) / steps);
    const b = Math.round(startRgb.b + ((endRgb.b - startRgb.b) * i) / steps);
    gradient.push(rgbToHex({ r, g, b }));
  }

  return gradient;
}

export function changeBackgroundImageColor(isDark: boolean) {
  let from = getComputedStyle(document.documentElement).getPropertyValue("--p-surface-800");
  let to = getComputedStyle(document.documentElement).getPropertyValue("--p-surface-700");
  if (!isDark) {
    from = getComputedStyle(document.documentElement).getPropertyValue("--p-surface-100");
    to = getComputedStyle(document.documentElement).getPropertyValue("--p-surface-200");
  }
  const _gradient = generateGradientColors(from, to, 5);
}

export type InkDefinition = {
  code: string;
  name: string;
  color: {
    rgb: string;
    hex: string;
  };
};

const DEFAULT_RGB = "255,255,255";

const INK_NAME_BY_CODE: Record<string, string> = {
  C: "Cyan",
  M: "Magenta",
  Y: "Yellow",
  K: "Black",
  W: "White",
  W2: "White2",
  "W*": "PreWhite",
  O: "Orange",
  o: "LightOrange",
  R: "Red",
  G: "Green",
  B: "Blue",
  V: "Violet",
  c: "LightCyan",
  c2: "LightLightCyan",
  c3: "LightCyan_3",
  c4: "LightCyan_4",
  m: "LightMagenta",
  m2: "LightLightMagenta",
  m3: "LightMagenta_3",
  m4: "LightMagenta_4",
  y: "LightYellow",
  y2: "LightLightYellow",
  y3: "LightYellow_3",
  y4: "LightYellow_4",
  k: "LightBlack",
  g: "LightLightBlack",
  k3: "LightBlack_3",
  k4: "LightBlack_4",
  "C*": "Spot_Cyan",
  "M*": "Spot_Magenta",
  "Y*": "Spot_Yellow",
  "K*": "Spot_Black",
  "N*": "Spot_Brown",
  r: "LightRed",
  r2: "LightLightRed",
  t: "LightGreen",
  n2: "LightLightGreen",
  b: "LightBlue",
  b2: "LightLightBlue",
  "R*": "SpotRed",
  "G*": "SpotGreen",
  "B*": "SpotBlue",
  D: "Gold",
  X: "Mask",
  "L*": "Diluent",
  "L2*": "Diluent2",
  "L3*": "Diluent3",
  "L4*": "Diluent4",
  E: "Beige",
  "E*": "SpotBeige",
  I: "Pink",
  "MT*": "SpotMetallic",
  N: "Brown",
  A: "FluorCyan",
  H: "FluorMagenta",
  F: "FluorYellow",
  U: "FluorGreen",
  W3: "White3",
  W4: "White4",
  X2: "Mask2",
  X3: "Mask3",
  X4: "Mask4",
  "O*": "SpotOrange",
  n: "LightBrown",
  "F*": "Fixation",
  "S*": "Softener",
  S: "FluorOrange",
  "V*": "Varnish",
  "P*": "Primer",
  "I*": "Intensifier",
};

export const INK_RGB_BY_NAME: Record<string, string> = {
  cyan: "0, 255,255",
  magenta: "225,0,225",
  yellow: "255,255,0",
  black: "0,0,0",
  orange: "255,127,20",
  lightorange: "255,192,63",
  green: "0,255,0",
  blue: "0,0,255",
  violet: "127,0,255",
  red: "255,0,0",
  lightcyan: "127,255,255",
  lightlightcyan: "192,255,255",
  lightcyan_3: "215,255,255",
  lightcyan_4: "230,255,255",
  lightmagenta: "255,127,255",
  lightlightmagenta: "255,192,255",
  lightmagenta_3: "255,215,255",
  lightmagenta_4: "255,230,255",
  lightblack: "127,127,127",
  lightlightblack: "191, 191, 191",
  lightblack_3: "215,215,215",
  lightblack_4: "230,230,230",
  gold: "255,210,0",
  spot_cyan: "0,255,255",
  spot_magenta: "255,0,255",
  spot_yellow: "255,255,0",
  spot_black: "0,0,0",
  white: "250,250,250",
  white2: "250,250,250",
  mask: "192,192,192",
  spot_brown: "127,63,0",
  lightred: "255,127,127",
  lightgreen: "127,255,127",
  lightblue: "127,127,255",
  lightlightred: "255,190,190",
  lightlightgreen: "190,255,190",
  lightlightblue: "190,190,255",
  spotred: "255,0,0",
  spotgreen: "0,255,0",
  spotblue: "0,0,255",
  lightyellow: "255,255,50",
  lightlightyellow: "255,255,100",
  lightyellow_3: "255,255,150",
  lightyellow_4: "255,255,200",
  prewhite: "250,250,250",
  diluent: "240,240,240",
  diluent2: "240,240,240",
  diluent3: "240,240,240",
  diluent4: "240,240,240",
  brown: "127,63,0",
  beige: "250,200,100",
  pink: "255,127,255",
  spotbeige: "250,200,100",
  spotmetallic: "200,200,200",
  fluorcyan: "0,255,255",
  fluormagenta: "225,0,225",
  fluoryellow: "255,255,0",
  fluorgreen: "0,255,0",
  white3: "250,250,250",
  white4: "250,250,250",
  mask2: "192,192,192",
  mask3: "192,192,192",
  mask4: "192,192,192",
  spotorange: "255,127,20",
  lightbrown: "192,127,64",
  fixation: "200,200,200",
  softener: "200,200,200",
  fluororange: "255,127,0",
  varnish: "200,200,200",
  primer: "200,200,200",
  intensifier: "200,200,200",
};

function rgbStringToHex(rgb: string): string {
  const [r, g, b] = rgb.split(",").map((value) => Number(value.trim()));
  return rgbToHex({ r, g, b });
}

export const INKS_BY_CODE: Record<string, InkDefinition> = Object.fromEntries(
  Object.entries(INK_NAME_BY_CODE).map(([code, name]) => {
    const rgb = INK_RGB_BY_NAME[name.toLowerCase()] ?? DEFAULT_RGB;
    return [
      code,
      {
        code,
        name,
        color: {
          rgb,
          hex: rgbStringToHex(rgb),
        },
      },
    ];
  }),
) as Record<string, InkDefinition>;

export function inkNameFromInitials(inkInitial: string): string {
  return INKS_BY_CODE[inkInitial]?.name ?? inkInitial;
}

export function inkColorRGB(inkName: string): string {
  return INK_RGB_BY_NAME[inkName.toLowerCase()] ?? DEFAULT_RGB;
}
